import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";

export const MAX_COMPANY_NAME = 80;

interface StepEmpresaProps {
  readonly tenantId: string;
  readonly initialName: string;
  /** Registra a acao de salvar que o botao Continuar do wizard vai chamar. */
  readonly onRegisterSubmit: (submit: () => Promise<boolean>) => void;
}

/** Passo 1: nome da empresa (aparece na vitrine e nas mensagens). */
export function StepEmpresa({ tenantId, initialName, onRegisterSubmit }: StepEmpresaProps): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  onRegisterSubmit(async () => {
    const normalized = name.trim();
    if (!normalized || normalized.length > MAX_COMPANY_NAME) {
      setError(`Informe um nome de 1 a ${MAX_COMPANY_NAME} caracteres.`);
      return false;
    }
    setError(null);
    if (normalized === initialName.trim()) return true;
    const { error: updateError } = await supabase.from("tenants").update({ name: normalized }).eq("id", tenantId);
    if (updateError) {
      setError("Não foi possível salvar o nome. Tente de novo.");
      return false;
    }
    await queryClient.invalidateQueries({ queryKey: ["tenant-billing"] });
    return true;
  });

  return (
    <div className="space-y-3">
      <Label htmlFor="onboarding-company">Nome da empresa ou do seu canal</Label>
      <Input
        id="onboarding-company"
        value={name}
        maxLength={MAX_COMPANY_NAME}
        autoFocus
        onChange={(event) => setName(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? "onboarding-company-error" : "onboarding-company-hint"}
        placeholder="Ex.: Ofertas do Nik"
      />
      {error ? (
        <p id="onboarding-company-error" role="alert" className="text-xs text-destructive">{error}</p>
      ) : (
        <p id="onboarding-company-hint" className="text-xs text-muted-foreground">
          Esse nome aparece na sua vitrine e ajuda a identificar a conta. Você pode mudar depois.
        </p>
      )}
    </div>
  );
}
